import React, { useState, useCallback } from 'react';
import Canvas from '../Canvas';
import { drawPlateWithShapes } from './PlateDrawer';

/**
 * CutOutCanvasController Component
 * 
 * Handles all canvas interaction logic, coordinate transformations, and state management
 * for the cut-out configurator.
 */
const CutOutCanvasController = ({
    plateDimensions,
    shapes,
    selectedShapeId,
    onSelectShape,
    cutoffProperties,
    showGuidelines = false,
    focusedRadiusField = null,
}) => {
    const [hoveredShapeId, setHoveredShapeId] = useState(null);

    // Helper to convert plate units (mm) to canvas pixels based on scale
    const calcScaleAndOffsets = (canvasWidth, canvasHeight) => {
        const scale = Math.min(
            canvasWidth / plateDimensions.width,
            canvasHeight / plateDimensions.height,
        ) * 0.8;
        const plateWidthPx = plateDimensions.width * scale;
        const plateHeightPx = plateDimensions.height * scale;
        const plateX = canvasWidth / 2 - plateWidthPx / 2;
        const plateY = canvasHeight / 2 - plateHeightPx / 2;
        return { scale, plateWidthPx, plateHeightPx, plateX, plateY };
    };

    // Mouse move handler to detect hovered shape
    const handleMouseMove = useCallback(
        (e) => {
            const canvas = e.currentTarget;
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            const { scale, plateWidthPx, plateHeightPx, plateX, plateY } = calcScaleAndOffsets(
                rect.width,
                rect.height,
            );

            const scaleX = plateWidthPx / plateDimensions.width;
            const scaleY = plateHeightPx / plateDimensions.height;

            let foundId = null;
            for (const shape of shapes) {
                if (shape.shape === "RECTANGLE") {
                    const rectW = shape.width * scaleX;
                    const rectH = shape.height * scaleY;
                    const rectX = plateX + shape.x * scaleX;
                    const rectY = plateY + shape.y * scaleY;
                    if (
                        mouseX >= rectX &&
                        mouseX <= rectX + rectW &&
                        mouseY >= rectY &&
                        mouseY <= rectY + rectH
                    ) {
                        foundId = shape.identifier;
                        break;
                    }
                } else if (shape.shape === "CIRCLE") {
                    const uniformScale = Math.min(scaleX, scaleY);
                    const radius = shape.radius * uniformScale;
                    const circleX = plateX + shape.x * scaleX;
                    const circleY = plateY + shape.y * scaleY;
                    const dist = Math.hypot(mouseX - circleX, mouseY - circleY);
                    if (dist <= radius) {
                        foundId = shape.identifier;
                        break;
                    }
                }
            }

            if (foundId !== hoveredShapeId) {
                setHoveredShapeId(foundId);
            }
        },
        [shapes, hoveredShapeId, plateDimensions],
    );

    const handleMouseLeave = () => {
        if (hoveredShapeId !== null) setHoveredShapeId(null);
    };

    // Mouse click handler to select shapes
    const handleMouseClick = useCallback((e) => {
        const canvas = e.currentTarget;
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const { scale, plateWidthPx, plateHeightPx, plateX, plateY } = calcScaleAndOffsets(
            rect.width,
            rect.height,
        );

        const scaleX = plateWidthPx / plateDimensions.width;
        const scaleY = plateHeightPx / plateDimensions.height;

        let foundId = null;
        for (const shape of shapes) {
            if (shape.shape === "RECTANGLE") {
                const rectW = shape.width * scaleX;
                const rectH = shape.height * scaleY;
                const rectX = plateX + shape.x * scaleX;
                const rectY = plateY + shape.y * scaleY;
                if (
                    mouseX >= rectX &&
                    mouseX <= rectX + rectW &&
                    mouseY >= rectY &&
                    mouseY <= rectY + rectH
                ) {
                    foundId = shape.identifier;
                    break;
                }
            } else if (shape.shape === "CIRCLE") {
                const uniformScale = Math.min(scaleX, scaleY);
                const radius = shape.radius * uniformScale;
                const circleX = plateX + shape.x * scaleX;
                const circleY = plateY + shape.y * scaleY;
                const dist = Math.hypot(mouseX - circleX, mouseY - circleY);
                if (dist <= radius) {
                    foundId = shape.identifier;
                    break;
                }
            }
        }

        onSelectShape(foundId);
    }, [shapes, plateDimensions, onSelectShape]);

    // Create drawing function for the canvas
    const drawPlate = (ctx, canvasSize) => {
        drawPlateWithShapes(ctx, canvasSize, plateDimensions, shapes, selectedShapeId, hoveredShapeId, cutoffProperties, showGuidelines, focusedRadiusField);

        // Draw measurement overlay if a shape is hovered or selected
        const overlayShapeId = hoveredShapeId || selectedShapeId;
        if (!overlayShapeId) return;

        const overlayShape = shapes.find((s) => s.identifier === overlayShapeId);
        if (!overlayShape) return;

        const { scale, plateWidthPx, plateHeightPx, plateX, plateY } = calcScaleAndOffsets(
            canvasSize.width,
            canvasSize.height,
        );

        const scaleX = plateWidthPx / plateDimensions.width;
        const scaleY = plateHeightPx / plateDimensions.height;

        ctx.save();
        ctx.strokeStyle = "#FF8800";
        ctx.fillStyle = "#FF8800";
        ctx.lineWidth = 2;
        ctx.font = "12px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        if (overlayShape.shape === "RECTANGLE") {
            const rectW = overlayShape.width * scaleX;
            const rectH = overlayShape.height * scaleY;
            const rectX = plateX + overlayShape.x * scaleX;
            const rectY = plateY + overlayShape.y * scaleY;

            // Highlight rectangle
            ctx.strokeRect(rectX, rectY, rectW, rectH);

            // Width label
            ctx.fillText(`${overlayShape.width}mm`, rectX + rectW / 2, rectY - 10);
            
            // Height label (left side, rotated text)
            ctx.save();
            ctx.translate(rectX - 15, rectY + rectH / 2);
            ctx.rotate(-Math.PI / 2);
            ctx.fillText(`${overlayShape.height}mm`, 0, 0);
            ctx.restore();

            // Distance to plate edges
            const leftDist = overlayShape.x;
            const rightDist = plateDimensions.width - (overlayShape.x + overlayShape.width);
            const topDist = overlayShape.y;
            const bottomDist = plateDimensions.height - (overlayShape.y + overlayShape.height);

            // Calculate mid-points between the shape edge and the corresponding plate boundary
            const plateLeftPx = plateX;
            const plateRightPx = plateX + plateWidthPx;
            const plateTopPx = plateY;
            const plateBottomPx = plateY + plateHeightPx;

            const leftLabelX = (plateLeftPx + rectX) / 2;
            const rightLabelX = (rectX + rectW + plateRightPx) / 2;
            const topLabelY = (plateTopPx + rectY) / 2;
            const bottomLabelY = (rectY + rectH + plateBottomPx) / 2;

            ctx.fillText(`${leftDist}mm`, leftLabelX, rectY + rectH / 2);
            ctx.fillText(`${rightDist}mm`, rightLabelX, rectY + rectH / 2);
            ctx.fillText(`${topDist}mm`, rectX + rectW / 2, topLabelY);
            ctx.fillText(`${bottomDist}mm`, rectX + rectW / 2, bottomLabelY);

        } else if (overlayShape.shape === "CIRCLE") {
            const uniformScale = Math.min(scaleX, scaleY);
            const radius = overlayShape.radius * uniformScale;
            const circleX = plateX + overlayShape.x * scaleX;
            const circleY = plateY + overlayShape.y * scaleY;

            // Highlight circle
            ctx.beginPath();
            ctx.arc(circleX, circleY, radius, 0, 2 * Math.PI);
            ctx.stroke();

            const diameter = overlayShape.radius * 2;
            // Diameter labels
            ctx.fillText(`${diameter}mm`, circleX, circleY - radius - 15);

            // Distances to edges (in mm)
            const leftDist = overlayShape.x - overlayShape.radius;
            const rightDist = plateDimensions.width - (overlayShape.x + overlayShape.radius);
            const topDist = overlayShape.y - overlayShape.radius;
            const bottomDist = plateDimensions.height - (overlayShape.y + overlayShape.radius);

            // Calculate mid-points between the circle edge and the corresponding plate boundary
            const plateLeftPx = plateX;
            const plateRightPx = plateX + plateWidthPx;
            const plateTopPx = plateY;
            const plateBottomPx = plateY + plateHeightPx;

            const circleLeft = circleX - radius;
            const circleRight = circleX + radius;
            const circleTop = circleY - radius;
            const circleBottom = circleY + radius;

            const leftLabelX = (plateLeftPx + circleLeft) / 2;
            const rightLabelX = (plateRightPx + circleRight) / 2;
            const topLabelY = (plateTopPx + circleTop) / 2;
            const bottomLabelY = (plateBottomPx + circleBottom) / 2;

            ctx.fillText(`${leftDist}mm`, leftLabelX, circleY);
            ctx.fillText(`${rightDist}mm`, rightLabelX, circleY);
            ctx.fillText(`${topDist}mm`, circleX, topLabelY);
            ctx.fillText(`${bottomDist}mm`, circleX, bottomLabelY);
        }
        ctx.restore();
    };

    return (
        <Canvas
            drawFunction={drawPlate}
            drawDependencies={[plateDimensions, shapes, hoveredShapeId, selectedShapeId, cutoffProperties, showGuidelines, focusedRadiusField]}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleMouseClick}
        />
    );
};

export default CutOutCanvasController; 